/**
 * 发薪日工作流的**端侧纯逻辑**（F 域 F3 · `DR-052` · 批 87）
 *
 * ## 🔴 本文件**不算任何金额**
 *
 * 「本次分配后可花」是 `composePayday()` 的结论（服务端），端侧只把用户改的额度
 * **回传**给预演接口（`AC-F3.4` 的"金额可调"）。改完立刻重算 —— 这正是
 * `DR-052` D3 让**预演也接受 `allocations`** 的原因（铁律 94）。
 *
 * ## 🔴 `0` 与"没勾"的区别
 *
 * | 值 | 含义 | 落库 |
 * |---|---|---|
 * | 键不存在 | 这次不分配这个目标 | ❌ 不建行（`AC-F3.13`） |
 * | `0` | 同上（用户把金额清空了） | ❌ 不建行 |
 * | `>0` | 分配这么多 | ✅ |
 *
 * ⇒ 落账请求**只发 `>0` 的项**；预演请求发全部（含 0），这样服务端能算出"改后的可花"。
 */

import {
  AutoSaveMode,
  formatMoneyText,
  type Cents,
  type PaydayCandidateView,
  type PaydayConfirmResultView,
  type PaydayPreviewView,
} from '@jiaying/shared';
import { percentTextOf } from './goal.js';

// ═══════════════════════════ ① 展示文案 ═══════════════════════════

/** 必付项的类型标签（与 F2 的 `UpcomingKind` 同值；未知值原样回显） */
export function payableKindLabel(kind: string): string {
  switch (kind) {
    case 'creditCard':
      return '信用卡';
    case 'payable':
      return '待缴';
    case 'schedule':
      return '定期';
    default:
      return kind;
  }
}

/** 发薪日 → 「10 月 5 日」（**只切字符串，不做日期运算**） */
export function paydayText(payday: string): string {
  const month = Number(payday.slice(5, 7));
  const day = Number(payday.slice(8, 10));
  return `${String(month)} 月 ${String(day)} 日`;
}

/** 候选列表的一行副标题：「10 月 5 日 · 实发 ¥20,000.00」 */
export function candidateSubtitle(c: PaydayCandidateView): string {
  return `${paydayText(c.payday)} · 实发 ${formatMoneyText(c.netCents, c.currency)}`;
}

/** 自动攒配置的一句话说明（端侧表单回显用） */
export function autoSaveText(mode: AutoSaveMode, value: number, currency: string): string {
  switch (mode) {
    case AutoSaveMode.Off:
      return '不自动攒';
    case AutoSaveMode.Fixed:
      return `每期 ${formatMoneyText(value as Cents, currency)}`;
    case AutoSaveMode.Percent:
      // 🔴 复用  的整数搬家（端侧禁除法）；不另写一份
      return `每期按 ${percentTextOf(value)} 攒`;
    default:
      return '不自动攒';
  }
}

// ═══════════════════════════ ② 草稿（勾选 + 改额） ═══════════════════════════

/** 草稿 = `goalId → 金额（分）`；**键不存在 = 这次不分配** */
export interface PaydayDraft {
  readonly amounts: ReadonlyMap<string, number>;
}

export const EMPTY_DRAFT: PaydayDraft = { amounts: new Map() };

/**
 * 从预演初始化草稿：**默认勾选**所有"可分配且建议额 > 0"的目标。
 *
 * 🔴 不可分配的目标（未绑账户 / 币种不符 / 已达成）**不进草稿** ——
 * 它们的 `blockedReason` 在界面上单独显示，不给勾选框（不给"点了会失败"的开关）。
 */
export function draftFromPreview(preview: PaydayPreviewView): PaydayDraft {
  const amounts = new Map<string, number>();
  for (const g of preview.goals) {
    if (g.blockedReason !== null) continue;
    const planned = g.plannedCents as number;
    if (planned > 0) amounts.set(g.goalId, planned);
  }
  return { amounts };
}

/** 改一项的额度（`0` 等价于取消） */
export function withAmount(draft: PaydayDraft, goalId: string, cents: number): PaydayDraft {
  const amounts = new Map(draft.amounts);
  if (cents <= 0) amounts.delete(goalId);
  else amounts.set(goalId, cents);
  return { amounts };
}

/** 勾 / 取消一个目标（勾上 = 用建议额，没有建议额时用 1 分占位由用户改） */
export function toggleGoal(draft: PaydayDraft, goalId: string, suggested: number): PaydayDraft {
  if (draft.amounts.has(goalId)) return withAmount(draft, goalId, 0);
  return withAmount(draft, goalId, suggested > 0 ? suggested : 100);
}

/** 落账请求：**只发 `>0` 的项**（`AC-F3.13`：未勾选不落账） */
export function confirmAllocations(draft: PaydayDraft): { goalId: string; amountCents: number }[] {
  const out: { goalId: string; amountCents: number }[] = [];
  for (const [goalId, amountCents] of draft.amounts) {
    if (amountCents > 0) out.push({ goalId, amountCents });
  }
  return out;
}

/** 预演请求：发**全部**（含 `0`），这样服务端能算出"取消之后"的可花 */
export function previewAllocations(draft: PaydayDraft): { goalId: string; amountCents: number }[] {
  return [...draft.amounts.entries()].map(([goalId, amountCents]) => ({ goalId, amountCents }));
}

/** 至少勾一项才能确认（`PaydayConfirmRequestSchema` 的 `min(1)` 与此同义） */
export function canConfirm(draft: PaydayDraft): boolean {
  return confirmAllocations(draft).length > 0;
}

/** 草稿是否与"服务端建议"不同（决定要不要重新预演） */
export function draftDiffersFromPreview(draft: PaydayDraft, preview: PaydayPreviewView): boolean {
  const suggested = new Map<string, number>();
  for (const g of preview.goals) {
    if (g.blockedReason === null && (g.plannedCents as number) > 0) {
      suggested.set(g.goalId, g.plannedCents as number);
    }
  }
  if (suggested.size !== draft.amounts.size) return true;
  for (const [goalId, cents] of draft.amounts) {
    if (suggested.get(goalId) !== cents) return true;
  }
  return false;
}

// ═══════════════════════════ ③ 落账结果 ═══════════════════════════

/** 失败项（`AC-F3.7`：必须逐项显示原因） */
export function failedRows(result: PaydayConfirmResultView): PaydayConfirmResultView['rows'][number][] {
  return result.rows.filter((r) => r.status === 'failed');
}

/** 落账后的一句话（纯拼接服务端给的数字，不重算） */
export function confirmSummary(result: PaydayConfirmResultView): string {
  const parts: string[] = [];
  if (result.created > 0) parts.push(`已分配 ${String(result.created)} 个目标`);
  if (result.skipped > 0) parts.push(`${String(result.skipped)} 个此前已分配过`);
  if (result.failed > 0) parts.push(`${String(result.failed)} 个失败`);
  return parts.length === 0 ? '这次没有分配' : `${parts.join('，')}。`;
}
